import { z } from 'zod';
import { ALL_COMMERCIAL_MODES, ALL_LIFETIME_FREE_CATEGORIES, LifetimeFreeCategory } from './enums';

/**
 * #679 F7 (#738) — migração da cortesia e dos demos para o tipo comercial,
 * em etapas (§4.5 da #679). Contrato entre os callables
 * `auditCourtesyMigration`/`migrateCourtesy` e a tela
 * `/billing/courtesy-migration` do admin.
 *
 * **Invariante de toda a migração: em nenhum instante, em nenhuma versão de
 * app, um tenant cortesia vê o paywall.** O gate legado (apps antigos) libera
 * pela presença de `external_subscription_id`; o novo, pelo tipo comercial.
 * Os dois sinais ficam verdadeiros ao mesmo tempo até a última etapa.
 */
export const CourtesyMigrationStage = {
  /** E1 — grava COURTESY/DEMO a partir do cupom. Não toca o Stripe nem o id. */
  MARK: 'MARK',
  /** E1 — grava DIRECT explícito no resto (o Firestore não casa campo ausente, C5). */
  MARK_DIRECT: 'MARK_DIRECT',
  /** E3 — cancela a assinatura com cupom no Stripe. O id FICA no doc. Aval por lote. */
  CANCEL_GATEWAY: 'CANCEL_GATEWAY',
  /** E4 — tira o id do doc, só sem dispositivo ativo com o gate legado. */
  CLEAR_LEGACY_ID: 'CLEAR_LEGACY_ID',
} as const;
export type CourtesyMigrationStage =
  (typeof CourtesyMigrationStage)[keyof typeof CourtesyMigrationStage];
export const ALL_COURTESY_MIGRATION_STAGES = Object.values(CourtesyMigrationStage) as [
  CourtesyMigrationStage,
  ...CourtesyMigrationStage[],
];

/** Etapas que agem no Stripe ou tiram o sinal legado exigem lista explícita. */
export function stageRequiresExplicitTenants(stage: CourtesyMigrationStage): boolean {
  return (
    stage === CourtesyMigrationStage.CANCEL_GATEWAY ||
    stage === CourtesyMigrationStage.CLEAR_LEGACY_ID
  );
}

export const MigrateCourtesyInput = z
  .object({
    stage: z.enum(ALL_COURTESY_MIGRATION_STAGES),
    /** Vazio = todos os elegíveis — só nas etapas que não mexem no Stripe. */
    tenantIds: z.array(z.string().trim().min(1)).max(500).default([]),
    dryRun: z.boolean().default(true),
    reason: z.string().trim().min(10).max(500),
    /** CLEAR_LEGACY_ID: build mínimo com o gate novo, por plataforma nativa. */
    minBuilds: z
      .object({
        android: z.number().int().positive(),
        ios: z.number().int().positive(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (stageRequiresExplicitTenants(data.stage) && data.tenantIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['tenantIds'],
        message: 'Esta etapa exige a lista explícita de tenants (aval por lote).',
      });
    }
    if (data.stage === CourtesyMigrationStage.CLEAR_LEGACY_ID && data.minBuilds === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['minBuilds'],
        message: 'CLEAR_LEGACY_ID exige o build mínimo com o gate novo (Android e iOS).',
      });
    }
  });
export type MigrateCourtesyInput = z.infer<typeof MigrateCourtesyInput>;

const Snapshot = z.record(z.string(), z.unknown());

export const MigrateCourtesyRow = z.object({
  tenantId: z.string(),
  before: Snapshot,
  after: Snapshot,
  /** CANCEL_GATEWAY aplicado: desfecho no Stripe. */
  gateway: z.string().nullable(),
});
export type MigrateCourtesyRow = z.infer<typeof MigrateCourtesyRow>;

export const MigrateCourtesyOutput = z.object({
  stage: z.enum(ALL_COURTESY_MIGRATION_STAGES),
  dryRun: z.boolean(),
  /** Simulado: o que seria gravado. Aplicado: o que foi. */
  rows: z.array(MigrateCourtesyRow),
  skipped: z.array(z.object({ tenantId: z.string(), why: z.string() })),
  failed: z.array(z.object({ tenantId: z.string(), error: z.string() })),
});
export type MigrateCourtesyOutput = z.infer<typeof MigrateCourtesyOutput>;

export const CourtesyAuditDevice = z.object({
  deviceId: z.string(),
  platform: z.string(),
  appVersion: z.string(),
  /** Build extraído de `appVersion` (`2026.09.25+244` → 244), ou null. */
  build: z.number().int().nullable(),
  lastSeenAt: z.number().nullable(),
});
export type CourtesyAuditDevice = z.infer<typeof CourtesyAuditDevice>;

export const CourtesyAuditRow = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  /** `commercial_mode` gravado (null = ausente). */
  storedMode: z.enum(ALL_COMMERCIAL_MODES).nullable(),
  /** Tipo efetivo (gravado, ou o cupom legado). */
  effectiveMode: z.enum(ALL_COMMERCIAL_MODES),
  isDemoFlag: z.boolean(),
  externalSubscriptionId: z.string().nullable(),
  legacyExternalSubscriptionId: z.string().nullable(),
  /**
   * Status da assinatura do doc no Stripe; null = inexistente (ou sem id);
   * 'unavailable' = sem chave/erro; `unverifiable_<provider>` (ex.:
   * `unverifiable_asaas`) = id de outro gateway, não consultado (#795, BIL-2).
   */
  gatewayStatus: z.string().nullable(),
  /** Gate dos apps antigos: libera pela presença do id. */
  legacyGateOpen: z.boolean(),
  /** Gate novo (`needsPayment`): libera pelo tipo comercial. */
  newGateOpen: z.boolean(),
  devices: z.array(CourtesyAuditDevice),
});
export type CourtesyAuditRow = z.infer<typeof CourtesyAuditRow>;

export const AuditCourtesyMigrationOutput = z.object({
  generatedAt: z.number(),
  /** Tenants não-diretos (efetivo) ou já com `courtesy{}`. */
  rows: z.array(CourtesyAuditRow),
  /** Venda direta ainda sem `commercial_mode` gravado — o alvo do MARK_DIRECT. */
  directWithoutStoredMode: z.array(z.string()),
});
export type AuditCourtesyMigrationOutput = z.infer<typeof AuditCourtesyMigrationOutput>;

/**
 * Build de um `appVersion` (`2026.09.25+244` → 244). Formato das duas
 * plataformas nativas; sem `+<número>`, null — e versão desconhecida conta
 * como antiga na E4, que é o lado seguro.
 */
export function buildNumberOf(appVersion: string): number | null {
  const m = /\+(\d+)\s*$/.exec(appVersion);
  return m ? Number(m[1]) : null;
}

/** Janela de "dispositivo ativo" para a E4: aparelho parado há mais que isto não trava. */
export const COURTESY_ACTIVE_DEVICE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * O dispositivo ainda roda o gate legado? Só Android e iOS têm gate legado
 * instalado (o web é sempre o publicado). Versão ilegível conta como legada, e
 * aparelho sem heartbeat registrado também (não dá para saber quando rodou).
 * Aparelho com heartbeat mais velho que a janela não conta — senão um celular
 * esquecido numa gaveta travaria a E4 para sempre.
 */
export function deviceBlocksLegacyClear(
  device: Pick<CourtesyAuditDevice, 'platform' | 'build' | 'lastSeenAt'>,
  minBuilds: { android: number; ios: number },
  now: number,
): boolean {
  if (device.platform !== 'android' && device.platform !== 'ios') return false;
  if (device.lastSeenAt !== null && now - device.lastSeenAt > COURTESY_ACTIVE_DEVICE_WINDOW_MS) {
    return false;
  }
  const min = device.platform === 'android' ? minBuilds.android : minBuilds.ios;
  return device.build === null || device.build < min;
}

/** Rollback da E3 (`restoreLegacyCourtesySubscription`), até a E4. */
export const RestoreLegacyCourtesyInput = z.object({
  tenantId: z.string().trim().min(1),
  reason: z.string().trim().min(1).max(500),
});
export type RestoreLegacyCourtesyInput = z.infer<typeof RestoreLegacyCourtesyInput>;

/**
 * Categorias oferecidas ao conceder cortesia: todas menos DEMO. Demo é tipo
 * comercial próprio (interruptor de demo); `ApplyLifetimeFreeInput` e
 * `CreateCourtesyTenantInput` recusam DEMO no servidor também.
 */
export const COURTESY_GRANT_CATEGORIES = ALL_LIFETIME_FREE_CATEGORIES.filter(
  (c) => c !== LifetimeFreeCategory.DEMO,
);
